import React from 'react';
import { useRouter } from 'next/router';

import { Page } from '@tager/web-components';
import { FETCH_STATUSES, Nullable } from '@tager/web-core';

import Layout from '@/components/Layout';
import { CatalogCategoryType } from '@/typings/model';
import NotFoundPage from '@/pages/404';
import {
  convertAliasToPathQuery,
  convertErrorToProps,
  convertSlugToPath,
  getCurrentPath,
} from '@/utils/common';
import ErrorPage from '@/pages/_error';
import { CustomApp_PageContext } from '@/typings/hocs';
import { getSharedThunkList } from '@/utils/thunks';
import {
  getCategoryByAliasThunk,
  selectCategoryByAlias,
} from '@/store/reducers/pages/catalog/catalog';
import Catalog from '@/modules/Catalog';

type Props =
  | {
      pageType: 'CATALOG_PAGE';
      category: Nullable<CatalogCategoryType>;
    }
  | {
      pageType: 'NOT_FOUND';
    }
  | {
      pageType: 'ERROR_PAGE';
      error: Error;
    };

function CatalogPage(Props: Props) {
  const router = useRouter();

  if (Props.pageType === 'NOT_FOUND') {
    return <NotFoundPage />;
  }
  if (Props.pageType === 'ERROR_PAGE') {
    return <ErrorPage {...convertErrorToProps(Props.error)} />;
  }
  const currentPath = getCurrentPath(router);
  const cannonicalPath = process.env.NEXT_PUBLIC_ORIGIN + currentPath;

  return (
    <Page
      title={Props.category?.pageTitle}
      description={Props.category?.pageDescription}
      canonicalUrl={cannonicalPath}
    >
      <Layout>
        <Catalog />
      </Layout>
    </Page>
  );
}

export default CatalogPage;

CatalogPage.getInitialProps = async ({
  res,
  query,
  store,
}: CustomApp_PageContext): Promise<Props> => {
  try {
    const alias = convertAliasToPathQuery(convertSlugToPath(query.alias));
    await store.dispatch(getCategoryByAliasThunk(alias));
    const categoryResourceDate = selectCategoryByAlias(store.getState(), alias);
    if (categoryResourceDate.status === FETCH_STATUSES.FAILURE) {
      if (res) {
        res.statusCode = 404;
      }
      return {
        pageType: 'NOT_FOUND',
      };
    }

    await Promise.all([...getSharedThunkList(store.dispatch)]);

    return {
      pageType: 'CATALOG_PAGE',
      category: categoryResourceDate.data,
    };
  } catch (error) {
    return {
      pageType: 'ERROR_PAGE',
      error,
    };
  }
};
